import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Form, Row, Col, Badge, Alert, Spinner, Table } from 'react-bootstrap';
import api from '../../api/axios';
import { formatDateTime } from '../../utils/format';
import { FIELD_LABELS, renderValue, UTC_DRIFT_CUTOFF } from '../../components/audit/auditMeta';
import '../../components/audit/auditLog.css';

const PAGE_SIZE = 50;
const EMPTY_FILTERS = { shop_id: '', action: '', from: '', to: '' };

// details v2 한 건을 "라벨 · 이전 → 이후" 줄들로 그린다.
// v 가 없는 행(2026-10-20 이전)은 원문 JSON 을 그대로 보여준다 —
// 옛 포맷을 새 포맷인 척 해석하면 없는 정보를 지어내게 된다.
const DetailsCell = ({ details }) => {
    let d = details;
    if (typeof d === 'string') {
        try { d = JSON.parse(d); } catch (e) { return <pre className="al-raw">{details}</pre>; }
    }
    if (!d || typeof d !== 'object') return <span className="al-dim">-</span>;

    if (d.v !== 2) {
        const text = JSON.stringify(d, null, 1);
        if (text === '{}') return <span className="al-dim">-</span>;
        return (
            <>
                <Badge bg="secondary" className="al-legacy-badge">구 포맷</Badge>
                <pre className="al-raw">{text}</pre>
            </>
        );
    }

    const diffKeys = Object.keys(d.diff || {});
    const metaEntries = Object.entries(d.meta || {}).filter(([k]) => k !== 'noChange');

    return (
        <div className="al-details">
            {diffKeys.length === 0 && d.meta?.noChange && (
                <div className="al-dim">변경 없음 (저장만 실행)</div>
            )}
            {diffKeys.map(key => {
                const row = d.diff[key];
                return (
                    <div key={key} className="al-diff-row">
                        <span className="al-field">{FIELD_LABELS[key] || key}</span>
                        {row.masked_equal ? (
                            // 마스킹 때문에 앞뒤가 같은 글자가 된 경우. 같은 값을 두 번 쓰면
                            // "안 바뀐 걸 왜 로그에 남겼나" 로 읽히므로 문장으로 바꿔 쓴다.
                            <span className="al-vals">
                                <span className="al-from">{renderValue(key, row.from)}</span>
                                <span className="al-arrow">→</span>
                                <span className="al-to al-dim">다른 값으로 변경</span>
                            </span>
                        ) : (
                            <span className="al-vals">
                                <span className="al-from">{renderValue(key, row.from)}</span>
                                <span className="al-arrow">→</span>
                                <span className="al-to">{renderValue(key, row.to)}</span>
                            </span>
                        )}
                    </div>
                );
            })}
            {metaEntries.length > 0 && (
                <div className="al-meta">
                    {metaEntries.map(([k, v]) => (
                        <span key={k} className="al-meta-item">
                            {FIELD_LABELS[k] || k}: {renderValue(k, v)}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const AuditLogPage = () => {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [actions, setActions] = useState([]);
    const [shops, setShops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // 필터 선택지는 한 번만 받는다 — 로그가 쌓여도 목록 자체는 거의 안 변한다.
    useEffect(() => {
        api.get('/superadmin/audit-logs/actions')
            .then(res => setActions(res.data.data?.actions || []))
            .catch(() => setActions([]));
        api.get('/superadmin/audit-logs/shops')
            .then(res => setShops(res.data.data?.shops || []))
            .catch(() => setShops([]));
    }, []);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
            Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
            const res = await api.get(`/superadmin/audit-logs?${params.toString()}`);
            setItems(res.data.data?.items || []);
            setTotal(res.data.data?.total || 0);
        } catch (err) {
            setError(err.response?.data?.error || '로그를 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const setFilter = (patch) => { setPage(1); setFilters(f => ({ ...f, ...patch })); };
    const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <Container fluid className="py-4 al-page">
            <h2 className="mb-3" style={{ fontSize: '1.5rem' }}>감사 로그</h2>

            <Row className="g-2 mb-2">
                <Col xs={12} sm={6} md={3}>
                    <Form.Select size="sm" value={filters.shop_id}
                        onChange={e => setFilter({ shop_id: e.target.value })}>
                        <option value="">매장 전체</option>
                        {shops.map(s => (
                            <option key={s.id} value={s.id}>{s.shopName} ({s.cnt})</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <Form.Select size="sm" value={filters.action}
                        onChange={e => setFilter({ action: e.target.value })}>
                        <option value="">동작 전체</option>
                        {/* prefix 매칭이므로 그룹(접두어)도 선택지로 올린다 */}
                        {[...new Set(actions.map(a => a.action.split(':')[0]))].sort().map(g => (
                            <option key={`g-${g}`} value={`${g}:`}>{g}: (전체)</option>
                        ))}
                        {actions.map(a => (
                            <option key={a.action} value={a.action}>{a.action} ({a.cnt})</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col xs={6} sm={3} md={2}>
                    <Form.Control size="sm" type="date" value={filters.from}
                        onChange={e => setFilter({ from: e.target.value })} />
                </Col>
                <Col xs={6} sm={3} md={2}>
                    <Form.Control size="sm" type="date" value={filters.to}
                        onChange={e => setFilter({ to: e.target.value })} />
                </Col>
                <Col xs={12} md={2} className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" className="text-nowrap"
                        onClick={() => { setPage(1); setFilters(EMPTY_FILTERS); }}>
                        초기화
                    </Button>
                    <span className="al-dim d-flex align-items-center text-nowrap">총 {total.toLocaleString()}건</span>
                </Col>
            </Row>

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : items.length === 0 ? (
                <Card><Card.Body className="text-center text-muted py-5">조건에 맞는 로그가 없습니다.</Card.Body></Card>
            ) : (
                <Card>
                    <Table responsive className="mb-0 al-table">
                        <thead>
                            <tr>
                                <th>시각</th>
                                <th>매장</th>
                                <th>사용자</th>
                                <th>동작</th>
                                <th>대상</th>
                                <th>변경 내용</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(log => (
                                <tr key={log.id}>
                                    <td className="al-nowrap">
                                        {formatDateTime(log.timestamp)}
                                        {/* 세션 타임존을 KST 로 고정한 2026-10-13 이전 행은 UTC 로 저장됐다.
                                            조용히 +9h 보정하면 거짓이 되므로(어느 행이 밀렸는지 확정할 수 없다)
                                            표시만 한다. */}
                                        {String(log.timestamp) < UTC_DRIFT_CUTOFF && (
                                            <Badge bg="warning" className="ms-1 al-utc-badge"
                                                title="2026-10-13 이전 로그는 UTC 로 저장돼 9시간 빠르게 보일 수 있습니다">
                                                UTC?
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="al-nowrap">{log.shopName || (log.shop_id ? `#${log.shop_id}` : '-')}</td>
                                    {/* 접속지를 계정 칸 아래에 쌓는다. 7번째 열로 빼면 표가 그만큼
                                        넓어지는데, 둘은 같이 읽는 값이라("누가 어디서") 한 칸이 맞다.
                                        마이그36 이전 행과 크론·스크립트(req 없음)는 NULL 이라 '-' 로 뜬다. */}
                                    <td className="al-nowrap">
                                        {log.userName}
                                        <div className="al-dim al-ip">{log.ip || '-'}</div>
                                    </td>
                                    <td className="al-nowrap"><code className="al-action">{log.action}</code></td>
                                    <td className="al-nowrap al-dim">
                                        {log.targetType || '-'}{log.targetId ? ` #${log.targetId}` : ''}
                                    </td>
                                    <td><DetailsCell details={log.details} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}

            {lastPage > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                    <Button size="sm" variant="outline-secondary" disabled={page <= 1}
                        onClick={() => setPage(1)}>처음</Button>
                    <Button size="sm" variant="outline-secondary" disabled={page <= 1}
                        onClick={() => setPage(p => p - 1)}>이전</Button>
                    <span className="al-dim">{page} / {lastPage}</span>
                    <Button size="sm" variant="outline-secondary" disabled={page >= lastPage}
                        onClick={() => setPage(p => p + 1)}>다음</Button>
                </div>
            )}
        </Container>
    );
};

export default AuditLogPage;
